import { Component, HostListener, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';
import { Subscription } from 'rxjs/Subscription';
import { forkJoin } from 'rxjs/observable/forkJoin';
import * as _ from 'lodash';
import * as moment from 'moment';
import { CampaignsService } from 'app/shared/services/campaigns.service';
import { SharedService } from 'app/shared/services/shared.service';
import { UserService } from 'app/shared/services/user.service';
import { ModalsService } from 'app/shared/services/modals.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { HttpService } from 'app/shared/services/http.service';
import { SupplyService } from 'app/shared/services/supply.service';
import { DemandService } from 'app/shared/services/demand.service';
import { isNull } from 'util';

@AutoUnsubscribe()
@Component({
  selector: 'cedato-new-campaign',
  templateUrl: './new-campaign.component.html',
  styleUrls: ['./new-campaign.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NewCampaignComponent implements OnInit {
  public allSections: Array<any>;
  public queryParams: any;
  public editMode: boolean;
  public selectedItems: Array<any>;
  public selectedItemsIndexed: any;
  public subscriptions: Array<Subscription>;
  public routeParams: any;
  public campaignParams: any;
  public isLoading: boolean;
  public isLoadingEntities: boolean;
  public activeSection: number;
  public errorInputs: Array<string>;
  public assignedColumnsSettings: Array<any>;
  public assignedGridData: Array<any>;
  public assignedDataDisplayed: Array<any>;
  public assignedItemsFilter: any;
  public isLoadedInactiveItems: boolean;
  public selectedCampaignTypeIndex: number;
  public assignedTabsData: Array<any>;
  public assignedSelectedTab: number;
  public enableSelect: boolean;
  public campaignsSchedule: any;
  public hoursStartList: Array<any>;
  public hoursEndList: Array<any>;
  public isGridError: boolean;
  public backLink: string;
  public campaignCurrency: string;
  public enableSelectAll: boolean;
  public budgetMin: number;
  public isRunning: boolean;
  public isFinished: boolean;
  public emptyStartDateLabel: string;
  public emptyEndDateLabel: string;
  public emptyValues: Array<any>;
  public _campaignType: number;
  public _selectedItems: Array<any>;

  constructor(
    public campaignsService: CampaignsService,
    public route: ActivatedRoute,
    public dictionaryService: DictionaryService,
    public httpService: HttpService,
    public modalsService: ModalsService,
    public sharedService: SharedService,
    public userService: UserService,
    public supplyService: SupplyService,
    public demandService: DemandService,
    public router: Router
  ) {
    this.allSections = [
      { name: 'SETTINGS', value: 'campaignSettings', toggleStatus: true },
      { name: 'ITEMS', value: 'campaignAssignments', toggleStatus: true },
    ];
    this.editMode = false;
    this.selectedItems = [];
    this.selectedItemsIndexed = {};
    this.subscriptions = [];
    this.queryParams = {};
    this.routeParams = {};
    this.isLoading = false;
    this.isLoadingEntities = false;
    this.activeSection = 0;
    this.errorInputs = [];
    this.assignedColumnsSettings = this.campaignsService.assignedColumnsSettings;
    this.campaignParams = _.cloneDeep(this.campaignsService.defaultCampaignParams);
    this.assignedGridData = [];
    this.assignedDataDisplayed = [];
    this.assignedItemsFilter = { active: [true] };
    this.campaignsSchedule = {};
    this.selectedCampaignTypeIndex = -1;
    this.isLoadedInactiveItems = false;
    this.isGridError = false;
    this.backLink = '/campaigns/manage';
    this.hoursStartList = [];
    this.hoursEndList = [];
    this.campaignCurrency = this.dictionaryService.getCurrency(1);
    this._campaignType = -1;
    this._selectedItems = [];
    this.emptyValues = ['0', ''];
    this.assignedSelectedTab = 0;
    this.budgetMin = 10;
    this.enableSelect = true;
    this.enableSelectAll = true;
    this.isRunning = false;
    this.isFinished = false;
    this.emptyStartDateLabel = this.dictionaryService.get('SELECT_START_DATE');
    this.emptyEndDateLabel = this.dictionaryService.get('SELECT_END_DATE');
    this.assignedTabsData = [
      { title: this.dictionaryService.get('VIEW_ELIGIBLE') },
      { title: this.dictionaryService.get('VIEW_INELIGIBLE') },
    ];
    this.hoursEndList.push(this.dictionaryService.get('DAYPART_SELECT_ENDTIME'));
    for (let hourIndex = 0; hourIndex < this.sharedService.targetingLookups.hours12Format.length; hourIndex++) {
      this.hoursEndList.push(_.cloneDeep(this.sharedService.targetingLookups.hours12Format)[hourIndex % 24]);
    }
    this.setStartTime(true);
  }

  ngOnInit() {
    if (!this.userService.getFeatureState('manage_campaigns')) {
      this.router.navigate(['/']);
      return;
    }

    this.subscriptions.push(
      this.sharedService.getMessagesBus().subscribe((response: any) => {
        if (response.type === 'GRID_VALUE_CHANGE') {
          this.changeSelectionOfOneAssignment(response.data);
        } else if (response.type === 'GRID_SELECTION_CHANGE_ATTEMPT') {
          this.changeSelectionOfAllAssignments(response.data);
        } else if (response.type === 'NETWORK_CHANGE') {
          this.errorInputs = [];
          if (!this.editMode) {
            if (!this.userService.getFeatureState('manage_campaigns')) {
              this.router.navigate(['/']);
            } else {
              this.isLoading = true;
              this.assignedGridData = [];
              this.assignedDataDisplayed = [];
              this.initialize();
            }
          } else {
            this.userService.getFeatureState('manage_campaigns')
              ? this.router.navigate(['campaigns/manage'])
              : this.router.navigate(['/']);
          }
        }
      })
    );

    this.subscriptions.push(
      this.route.params.subscribe((params: any) => {
        if (params.id > 0) {
          this.routeParams.id = params.id;
          this.editMode = true;
          this.emptyStartDateLabel = '';
          this.emptyEndDateLabel = '';
          this.sharedService.setPageTitle('EDIT_CAMPAIGN');
          this.parseReportData();
        } else {
          this.sharedService.setPageTitle('NEW_CAMPAIGN');
          this.initialize();
        }
      })
    );
  }

  ngOnDestroy() {}

  initialize() {
    const find: any = _.find(this.campaignsService.assignmentTypes, { id: +this.campaignParams.type });
    this.campaignParams.startTime = this.hoursStartList[0];
    this.campaignParams.endTime = this.hoursEndList[0];
    this.campaignParams.startTimeStamp = new Date(this.campaignParams.start).getTime() / 1000;
    this.campaignParams.endTimeStamp = new Date(this.campaignParams.end).getTime() / 1000;
    if (find) { this.selectedCampaignTypeIndex = _.findIndex(this.campaignsService.assignmentTypes, find); }

    this.getAssignmentsList();
  }

  parseReportData() {
    this.isLoadingEntities = true;

    this.getCampaign().subscribe(
      (response) => {
        if (response && response.data && response.data.id) {
          this.formatCampaignData(response.data).then((res) => {
            this.getAssignmentsList();
          });
        } else {
          this.onError(true);
        }
      },
      (error: any) => {
        this.onError(false);
      }
    );
  }

  getAssignmentsList() {
    const params: any = this.buildCampaignParams('assignments');
    const assignmentsList: any = this.getAssignments(this.buildCampaignParams('assignments'));
    const assignmentsSchedule: any = this.getAssignmentsSchedule(this.buildCampaignParams('schedule'));

    if (!this.sharedService.isEmptyObj(params)) {
    } else {
      this.isLoadingEntities = false;
    }

    forkJoin([assignmentsList, assignmentsSchedule]).subscribe((response) => {
      // results[0] is assignmentsList
      // results[1] is assignmentsSchedule
      if (this.sharedService.checkResponseStatus(response[0]) && this.sharedService.checkResponseStatus(response[1])) {
        if (response[0] && response[0]['data'] && response[0]['data'].length && response[1] && response[1]['data']) {
          this.formatScheduleObject(response[1]['data']).then((res) => {
            this.parseAssignedGridData(response[0]['data']).then((res) => {
              this.refreshAssignedGrid();
              this.isLoadingEntities = false;
            });
          });
        } else {
          this.assignedGridData = [];
          this.isLoadingEntities = false;
        }
      } else {
        this.assignedGridData = [];
        this.isLoadingEntities = false;
        this.onError(false);
      }
    });
  }

  formatScheduleObject(data: any) {
    return new Promise((resolve, reject) => {
      if (data && data.length) {
        for (let i = 0; i < data.length; i++) {
          if (data[i] && data[i]['id'] && data[i]['budget'] && data[i]['start'] && data[i]['end']) {
            if (!_.isArray(this.campaignsSchedule[data[i]['id']])) {
              this.campaignsSchedule[data[i]['id']] = [];
            }

            this.campaignsSchedule[data[i]['id']].push({
              budget: data[i]['budget'],
              start: data[i]['start'],
              end: data[i]['end'],
            });
          }
        }
      }
      resolve();
    });
  }

  formatCampaignData(data: any) {
    return new Promise((resolve, reject) => {
      const find: any = _.find(this.campaignsService.assignmentTypes, { id: +data.type });
      const today: Date = new Date();
      let startTime: any;
      this.campaignParams = _.cloneDeep(data);
      this.campaignParams.isActive = +data.active === 1;
      this.campaignParams.startDate = this.campaignsService.getDate(data.start);
      if (moment(this.campaignParams.startDate).format('YYYY-MM-DD') !== moment(today).format('YYYY-MM-DD')) {
        this.setStartTime();
      }
      startTime = this.hoursEndList[this.campaignsService.getHour(data.start) + 1];
      if (this.hoursStartList.indexOf(startTime) > -1) {
      }
      this.campaignParams.startTime =
        this.hoursStartList.indexOf(startTime) > -1
          ? this.hoursStartList[this.hoursStartList.indexOf(startTime)]
          : this.hoursStartList[1];
      this.campaignParams.endDate = this.campaignsService.getDate(data.end);
      this.campaignParams.endTime = this.hoursEndList[this.campaignsService.getHour(data.end) + 1];
      this.campaignParams.startTimeStamp = new Date(this.campaignParams.start).getTime() / 1000;
      this.campaignParams.endTimeStamp = new Date(this.campaignParams.end).getTime() / 1000;
      this.campaignParams.selectedItems = data.entities ? data.entities.split(',') : [];
      this.onBudgetChange('daily', data.daily);
      this.onBudgetChange('hourly', data.hourly);

      if (find) { this.selectedCampaignTypeIndex = _.findIndex(this.campaignsService.assignmentTypes, find); }

      this.campaignCurrency = this.dictionaryService.getCurrency(+data.cid);
      this.isRunning = this.campaignsService.getRunningState(this.campaignParams);
      this.isFinished = this.campaignsService.isCampaignFinished(this.campaignParams);
      resolve();
    });
  }

  onCampaignTypeChange($event) {
    const prevReportType = _.cloneDeep(this.campaignParams.type);
    this.assignedItemsFilter = { active: [true] };
    this.campaignParams.selectedItems = [];
    this.isLoadedInactiveItems = false;
    const find: any = _.find(this.campaignsService.assignmentTypes, $event);
    let params: any;

    if (find) {
      this.campaignParams.type = find.id;
      this.selectedCampaignTypeIndex = _.findIndex(this.campaignsService.assignmentTypes, $event);

      if (prevReportType !== this.campaignParams.type) {
        params = this.buildCampaignParams('assignments');

        if (this.sharedService.isEmptyObj(params)) { return true; }

        this.isLoadingEntities = true;
        this.getAssignments(params).subscribe(
          (response) => {
            this.isGridError = false;

            if (response && response.data) {
              this.parseAssignedGridData(response.data).then((res) => {
                this.refreshAssignedGrid();
                this.isLoadingEntities = false;
              });
            } else {
              this.assignedGridData = [];
              this.isLoadingEntities = false;
            }
          },
          (error: any) => {
            this.isGridError = true;
            this.isLoadingEntities = false;
          }
        );
      }
    } else {
      this.campaignParams.type = 2;
      this.selectedCampaignTypeIndex = -1;
    }
  }

  buildCampaignParams(reportType: string = 'assignments') {
    const queryParams: any = {};
    let find: any;
    let selectedItems: Array<number>;

    switch (reportType) {
      case 'assignments':
        find = _.find(this.campaignsService.assignmentTypes, { id: +this.campaignParams.type });
        if (this.assignedItemsFilter.active.length === 1) {
          queryParams.active = this.assignedItemsFilter.active[0] ? 1 : 0;
        }

        queryParams.campaignType = find.id;
        break;

      case 'schedule':
        find = _.find(this.campaignsService.assignmentTypes, { id: +this.campaignParams.type });
        queryParams.type = find.id;
        queryParams.cid = this.campaignParams.id;
        break;

      case 'report':
        queryParams.id = this.campaignParams.id > 0 ? this.campaignParams.id : 0;
        queryParams.name = this.campaignParams.name;
        queryParams.active = this.campaignParams.isActive ? 1 : 0;
        queryParams.type = this.campaignParams.type;
        queryParams.cid = this.dictionaryService.getCurrencyId(this.campaignCurrency);
        if (this.campaignParams.hourly !== '') {
          queryParams.hourly = this.sharedService.strToInt(this.campaignParams.hourly);
        }
        if (this.campaignParams.daily !== '') {
          queryParams.daily = this.sharedService.strToInt(this.campaignParams.daily);
        }
        queryParams.start = this.formatDate();
        queryParams.end = this.formatDate('end');

        //Making selectedItems list
        selectedItems = _.map(
          _.filter(this.assignedGridData, function (o) {
            if (o.$$selected) { return o; }
          }),
          'id'
        );
        if (selectedItems.length !== this.assignedGridData.length) { queryParams.selectedItems = selectedItems; }

        break;

      default:
        break;
    }

    return queryParams;
  }

  formatDate(which: string = 'start') {
    let timeIndex = 0;
    const timeName = which === 'start' ? 'startTime' : 'endTime';
    const dateName = which === 'start' ? 'startDate' : 'endDate';
    const timestampName = which === 'start' ? 'startTimeStamp' : 'endTimeStamp';
    let resp: any;

    if (
      ![
        this.dictionaryService.get('DAYPART_SELECT_STARTTIME'),
        this.dictionaryService.get('DAYPART_SELECT_ENDTIME'),
      ].includes(this.campaignParams[timeName])
    ) {
      timeIndex = this.hoursEndList.indexOf(this.campaignParams[timeName]) - 1;
    }

    resp = moment(this.campaignParams[dateName]);

    if (timeIndex > 0) { resp = resp.add(timeIndex, 'h'); }

    this.campaignParams[timestampName] = new Date(resp.format('YYYY-MM-DD HH:mm:ss')).getTime() / 1000;

    return resp.format('YYYY-MM-DD HH:mm:ss');
  }

  parseAssignedGridData(assignments: Array<any>) {
    return new Promise((resolve, reject) => {
      let find: any = {};
      const urlTypeString: string = +this.campaignParams.type === 3 ? 'demand' : 'supply';

      assignments.forEach((one: any) => {
        find =
          +this.campaignParams.type === 3
            ? _.find(this.demandService.demandTypes, { id: +one.type })
            : _.find(this.supplyService.supplyTypes, { id: +one.type });

        one.$$selected =
          !this.campaignParams.selectedItems || this.campaignParams.selectedItems.length === 0
            ? false
            : this.campaignParams.selectedItems.includes(one.id);

        one.environment = {
          is_desktop: one.is_desktop === '1',
          is_mobile_web: one.is_mobile_web === '1',
          is_mobile_app: one.is_mobile_app === '1',
        };

        one.active = one.active === '1';
        one.type = find ? this.dictionaryService.get(find.name) : '';
        one.$$link = `/${urlTypeString}/manage/${one.id}`;
      });

      this.assignedGridData = _.cloneDeep(assignments);
      resolve();
    });
  }

  getCampaign() {
    return this.httpService.post('budget_manager/get', { id: this.routeParams.id });
  }

  saveCampaign(params: any) {
    return this.httpService.post('budget_manager/save', params);
  }

  getAssignments(params: any) {
    return this.httpService.post('budget_manager/getAssignments', params);
  }

  getAssignmentsSchedule(params: any) {
    return this.httpService.post('budget_manager/getAssignmentsSchedule', params);
  }

  toggleActiveState() {
    this.campaignParams.isActive = !this.campaignParams.isActive;
  }

  onSave() {
    const noErrors: boolean = this.validateForm();
    if (noErrors) {
      this.isLoading = true;
      this.isLoadingEntities = true;
      const params: any = this.buildCampaignParams('report');
      this.saveCampaign(params).subscribe(
        (response) => {
          this.isLoading = false;
          this.isLoadingEntities = false;
          if (response && response.success && response.id > 0) {
            if (this.editMode) {
              this.sharedService.showFlashMessage(this.dictionaryService.get('CAMPAIGN_SAVED_SUCCESSFULLY'), 'success');
            } else {
              this.sharedService.showFlashMessage(
                this.dictionaryService.get('CAMPAIGN_CREATED_SUCCESSFULLY'),
                'success'
              );
              this.router.navigate([`${this.backLink}/${response.id}`]);
            }
          } else {
            if (response.errors) {
              this.errorInputs = Object.keys(response.errors);
              if (response.errors.entities) {
                _.forEach(response.errors.entities, (one) => {
                  this.sharedService.showFlashMessage(one, 'error');
                });
              } else {
                this.sharedService.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
              }
            } else {
              this.sharedService.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
            }
          }
        },
        (error: any) => {
          this.isLoading = false;
          this.isLoadingEntities = false;
          this.sharedService.showFlashMessage(this.dictionaryService.get('AN_ERROR_HAS_OCCURRED'), 'error');
        }
      );
    }
  }

  cancel() {
    this.router.navigate([this.backLink]);
  }

  onError(isNotFound: boolean = false) {
    const message: string = isNotFound ? 'CAMPAIGN_NOT_FOUND' : 'AN_ERROR_HAS_OCCURRED';
    this.isGridError = !isNotFound;
    this.isLoading = false;
    this.isLoadingEntities = false;
    this.router.navigate([this.backLink]);
    this.sharedService.showFlashMessage(this.dictionaryService.get(message), 'error');
  }

  getSectionsPosition() {
    return window.scrollY > 72 ? 0 : 72;
  }

  getSectionsArrowPosition() {
    return this.activeSection && document.getElementById('sectionButton' + this.activeSection)
      ? document.getElementById('sectionButton' + this.activeSection).offsetTop
      : 0;
  }

  goToSection(id: number) {
    const section = document.getElementById(this.allSections[id].value);
    const yPosition = id > 0 ? section.offsetTop : 0;
    window.scroll({ top: yPosition, left: 0, behavior: 'smooth' });
    this.activeSection = id;
    if (!this.allSections[id].toggleStatus) {
      this.toggleSection(id);
    }
    setTimeout(() => {
      this.activeSection = id;
    }, 400);
  }

  toggleSection(id: number) {
    this.allSections[id].toggleStatus = !this.allSections[id].toggleStatus;
  }

  changeSelectionOfOneAssignment(item: any) {
    let find: any = {};
    if (item && item.dataItem && item.dataItem.hasOwnProperty('id') && item.dataItem.hasOwnProperty('$$selected')) {
      find = _.find(this.assignedGridData, { id: item.dataItem.id });
      if (find && find.id) {
        find['$$selected'] = item.dataItem['$$selected'];
      }
    }
  }

  changeSelectionOfAllAssignments(allSelectedState: boolean) {
    const title: string = allSelectedState ? 'SELECT_ALL' : 'UNSELECT_ALL';
    this.modalsService.showModal({
      title: this.dictionaryService.get(title),
      component: 'confirm-assign',
      data: { state: allSelectedState, type: 'items' },
    });
  }

  onAssignedItemsFilterChange($event) {
    if (!_.isEqual($event.active, this.assignedItemsFilter.active) && !this.isLoadedInactiveItems) {
      if ($event.active.includes(false)) { this.isLoadedInactiveItems = true; }

      this.assignedItemsFilter = $event;
      this.getAssignmentsList();
    }
  }

  dateChanged(date: any, range: string = 'start') {
    const today: string = moment().format('YYYY-MM-DD');
    switch (range) {
      case 'start':
        this.campaignParams.startDate = date;
        this.campaignParams.start = this.formatDate();
        this.emptyStartDateLabel = '';
        this.setStartTime(moment(date).format('YYYY-MM-DD') === today);
        break;

      case 'end':
        this.campaignParams.endDate = date;
        this.campaignParams.end = this.formatDate('end');
        this.emptyEndDateLabel = '';
        break;

      default:
        break;
    }
    this.refreshAssignedGrid();
  }

  onTimeChange(range: string = 'start') {
    switch (range) {
      case 'start':
        this.campaignParams.start = this.formatDate();
        break;

      case 'end':
        this.campaignParams.end = this.formatDate('end');
        break;

      default:
        break;
    }
    this.refreshAssignedGrid();
  }

  refreshAssignedGrid() {
    this.onAssignedTabSelected(this.assignedSelectedTab);
  }

  onAssignedTabSelected(id: number = -1) {
    const assignedData: Array<any> = _.cloneDeep(this.assignedGridData);
    const eligible: Array<any> = [];
    const ineligible: Array<any> = [];
    let isEligible: boolean;
    let thisColumn: any;

    for (let i = 0; i < assignedData.length; i++) {
      isEligible = true;
      if (this.campaignsSchedule[assignedData[i]['id']]) {
        if (this.campaignsSchedule[assignedData[i]['id']].length) {
          for (let j = 0; j < this.campaignsSchedule[assignedData[i]['id']].length; j++) {
            isEligible =
              (+this.campaignsSchedule[assignedData[i]['id']][j]['end'] > +this.campaignParams.startTimeStamp &&
                +this.campaignsSchedule[assignedData[i]['id']][j]['start'] > +this.campaignParams.endTimeStamp) ||
              (+this.campaignsSchedule[assignedData[i]['id']][j]['end'] < +this.campaignParams.startTimeStamp &&
                +this.campaignsSchedule[assignedData[i]['id']][j]['start'] < +this.campaignParams.endTimeStamp);

            if (!isEligible) { break; }
          }

          if (isEligible) {
            eligible.push(assignedData[i]);
          } else {
            ineligible.push(assignedData[i]);
          }
        }
      } else {
        eligible.push(assignedData[i]);
      }
    }

    // Display the relevant data
    thisColumn = _.find(this.assignedColumnsSettings, { key: '$$selected' });

    switch (id) {
      case 0:
        this.assignedDataDisplayed = _.cloneDeep(eligible);
        thisColumn.width = 192;
        thisColumn.resizable = true;
        this.enableSelectAll = true;
        this.enableSelect = true;
        break;
      case 1:
        this.assignedDataDisplayed = _.cloneDeep(ineligible);
        thisColumn.width = 0;
        thisColumn.resizable = false;
        this.enableSelectAll = false;
        this.enableSelect = false;
        break;
    }

    // Update the tabs' titles and the selected tab ID
    this.assignedTabsData = [
      { title: this.dictionaryService.get('VIEW_AVAILABLE') + ' (' + eligible.length + ')' },
      {
        title:
          this.dictionaryService.get('VIEW_UNAVAILABLE') + ' <span class="badText">(' + ineligible.length + ')</span>',
      },
    ];
    this.assignedSelectedTab = id;
  }

  onBudgetChange(id: string, value: any) {
    this.campaignParams[id] = '';
    if (isNull(value)) {
      this.campaignParams[id] = '';
    } else {
      const num: number = +value;
      const errStr: string = id === 'hourly' ? 'hourly_budget' : 'daily_budget';
      if (isNaN(num)) {
        this.errorInputs.push(errStr);
      } else {
        this.campaignParams[id] = num >= 0 ? this.sharedService.toLocaleString(num) : '';
        this.errorInputs = this.errorInputs.filter((err) => err !== errStr);
      }

      this.errorInputs = _.uniq(this.errorInputs);
    }
  }

  setStartTime(isToday: boolean = false) {
    const cutHours: number = +moment().utc().utcOffset(this.sharedService.offsetEST).format('h') + 1;
    this.hoursStartList = [];
    this.hoursStartList.push(this.dictionaryService.get('DAYPART_SELECT_STARTTIME'));
    for (let hourIndex = 0; hourIndex < this.sharedService.targetingLookups.hours12Format.length; hourIndex++) {
      this.hoursStartList.push(_.cloneDeep(this.sharedService.targetingLookups.hours12Format)[hourIndex % 24]);
    }

    if (isToday) {
      this.hoursStartList.splice(1, cutHours);
    } else {
      if (!_.find(this.hoursStartList, this.campaignParams.startTime)) {
        this.campaignParams.startTime = this.hoursStartList[0];
      }
    }
  }

  validateForm() {
    let valid = true;
    this.errorInputs = [];

    if (this.sharedService.isEmpty(this.campaignParams.name)) {
      valid = false;
      this.errorInputs.push('name');
    }

    if (this.emptyStartDateLabel !== '') {
      valid = false;
      this.errorInputs.push('start_date_is_not_set');
    }

    if (this.emptyEndDateLabel !== '') {
      valid = false;
      this.errorInputs.push('end_date_is_not_set');
    }

    if (this.campaignParams.startTime === this.hoursStartList[0]) {
      valid = false;
      this.errorInputs.push('start_time_is_not_set');
    }

    if (this.campaignParams.start >= this.campaignParams.end) {
      this.sharedService.showFlashMessage(this.dictionaryService.get('START_DATE_WRONG'), 'error');
    }

    if (this.campaignParams.endTime === this.hoursEndList[0]) {
      valid = false;
      this.errorInputs.push('end_time_is_not_set');
    }

    if (!this.emptyValues.includes(this.campaignParams.daily) && +this.campaignParams.daily < this.budgetMin) {
      valid = false;
      this.errorInputs.push('daily_min');
    }

    if (!this.emptyValues.includes(this.campaignParams.hourly) && +this.campaignParams.hourly < this.budgetMin) {
      valid = false;
      this.errorInputs.push('hourly_min');
    }

    if (this.emptyValues.includes(this.campaignParams.daily) && this.emptyValues.includes(this.campaignParams.hourly)) {
      valid = false;
      this.errorInputs.push('common_budget');
    }

    if (
      !this.emptyValues.includes(this.campaignParams.daily) &&
      !this.emptyValues.includes(this.campaignParams.hourly) &&
      +this.campaignParams.hourly >= +this.campaignParams.daily
    ) {
      valid = false;
      this.errorInputs.push('budget_compare');
    }

    this.errorInputs = _.uniq(this.errorInputs);
    if (!valid) {
      this.sharedService.showFlashMessage(this.dictionaryService.get('NEW_ENTITY_GENERAL_ERROR'), 'error');
    }
    return valid;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick($event: any) {
    let index = 0;
    let clickedOnNetworkButton = false;
    let clickedOnNetworkList = false;
    const path: any =
      $event.path || ($event.composedPath && $event.composedPath()) || this.sharedService.composedPath($event.target);

    /* Close change network dropdown */
    index = 0;
    while (index < path.length && !clickedOnNetworkButton && !clickedOnNetworkList) {
      clickedOnNetworkButton = path[index].id === 'reportNetworkToggleButton';
      clickedOnNetworkList = path[index].id === 'changeReportNetworkPopUp';
      if (!clickedOnNetworkButton && !clickedOnNetworkList) {
        index++;
      }
    }
  }

  @HostListener('document:wheel', ['$event'])
  onScroll($event: any) {
    let found = false;
    let index = 0;
    const scrollY: number = window.scrollY;
    const sections: HTMLElement = document.getElementById('sections');
    let currSection: HTMLElement;
    if (sections) {
      while (!found && index < this.allSections.length - 1) {
        currSection = document.getElementById(this.allSections[index].value);
        if (currSection) {
          found = currSection.offsetTop >= scrollY;
        }
        if (!found) {
          index++;
        }
      }
    }
    this.activeSection = index;
  }
}
